import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { WorkshopReference } from 'src/app/core/schema';
import { WorkshopService } from 'src/app/domain/workshop/services/workshop.service';
import { Unsubscribe } from '@angular/fire/firestore';

/**
 * Signal-based state for the current user's workshop references.
 *
 * Workshop references are stored at:
 * `partners/{partnerId}/users/{userEmail}/workshop_refs/{workshopId}`
 *
 * This replaces the NGXS `WorkshopsState` + `WorkshopsService` combination
 * from `rxjs-store/workshops/`.
 *
 * @feature shared
 */
@Injectable({ providedIn: 'root' })
export class MyWorkshopRefsState implements OnDestroy {
  private readonly workshopService = inject(WorkshopService);
  private workshopRefsUnsubscribe?: Unsubscribe;

  // ── Internal writable signals ──────────────────────────────────────────

  private readonly _workshopRefs = signal<WorkshopReference[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _loaded = signal<boolean>(false);

  // ── Public readonly signals ────────────────────────────────────────────

  readonly workshopRefs = this._workshopRefs.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  // ── Computed ───────────────────────────────────────────────────────────

  readonly workshopIds = computed(() => this._workshopRefs().map(r => r.workshopId));
  readonly workshopCount = computed(() => this._workshopRefs().length);
  readonly hasWorkshops = computed(() => this._workshopRefs().length > 0);

  // ── Lifecycle ──────────────────────────────────────────────────────────

  /**
   * Load workshop references for the current user with a real-time listener.
   */
  async loadWorkshopReferences(partnerId: string, userEmail: string): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    if (this.workshopRefsUnsubscribe) {
      this.workshopRefsUnsubscribe();
      this.workshopRefsUnsubscribe = undefined;
    }

    return new Promise((resolve, reject) => {
      let isInitialLoad = true;
      try {
        this.workshopRefsUnsubscribe = this.workshopService.subscribeToUserWorkshopReferences(
          partnerId,
          userEmail,
          (refs: WorkshopReference[]) => {
            this._workshopRefs.set(refs);
            this._loading.set(false);
            this._loaded.set(true);
            if (isInitialLoad) {
              isInitialLoad = false;
              resolve();
            }
          },
        );
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : 'Failed to load workshop references';
        this._error.set(msg);
        this._loading.set(false);
        reject(error);
      }
    });
  }

  /** Clear all workshop references and tear down the listener. */
  clearWorkshopReferences(): void {
    if (this.workshopRefsUnsubscribe) {
      this.workshopRefsUnsubscribe();
      this.workshopRefsUnsubscribe = undefined;
    }
    this._workshopRefs.set([]);
    this._loading.set(false);
    this._error.set(null);
    this._loaded.set(false);
  }

  /** Get a specific workshop reference by ID. */
  getWorkshopRef(id: string): WorkshopReference | undefined {
    return this._workshopRefs().find(r => r.workshopId === id);
  }

  ngOnDestroy(): void {
    if (this.workshopRefsUnsubscribe) {
      this.workshopRefsUnsubscribe();
    }
  }
}
